import React, { FunctionComponent, useCallback, useState, useRef, useEffect } from 'react';
import { ChromePicker } from 'react-color';
import { Controller, useFormContext } from 'react-hook-form';
import { IColorInputProps } from './types';
import { settingsSelectors } from '../../../../../app/resource/settings/settings.selectors';
import { get } from 'lodash';
import { useSelect } from '../../../../../utils/selector.utils';
const DEFAULT_GREEN = '#1bc3a7';
const PICKER_HEIGHT = 300;

export const ColorInput: FunctionComponent<IColorInputProps> = ({
  id,
  name,
  defaultValue,
  placeholder,
  validation,
  label,
  description,
  disabled,
  resettableToDefaultColor = false,
}) => {
  const allSettings = useSelect(settingsSelectors.selectResourceList);
  const settings = allSettings.find((s) => s !== null) ?? null;

  const [color, setColor] = useState(defaultValue || settings?.primaryColor1 || DEFAULT_GREEN);
  const [visible, setVisible] = useState(false);
  const [popupPosition, setPopupPosition] = useState<'top' | 'bottom'>('top');
  const { control, errors } = useFormContext();
  const popupRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const resetButtonRef = useRef<HTMLDivElement>(null);

  // Calculate popup position based on available space
  const calculatePopupPosition = useCallback(() => {
    if (!triggerRef.current) {
      return;
    }

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const spaceAbove = triggerRect.top;
    const spaceBelow = viewportHeight - triggerRect.bottom;

    if (spaceAbove >= PICKER_HEIGHT || spaceAbove > spaceBelow) {
      setPopupPosition('top');
    } else {
      setPopupPosition('bottom');
    }
  }, []);

  // Toggle popup visibility
  const handleTogglePopup = useCallback(() => {
    if (disabled) {
      return;
    }

    if (!visible) {
      calculatePopupPosition();
    }
    setVisible((prev) => !prev);
  }, [disabled, visible, calculatePopupPosition]);

  // Close popup when clicking outside
  useEffect(() => {
    if (!visible) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Check if click is outside popup
      if (!popupRef.current?.contains(target)) {
        // Check if click is not on trigger or reset button
        const isTriggerClick = triggerRef.current?.contains(target);
        const isResetButtonClick = resetButtonRef.current?.contains(target);

        if (!isTriggerClick && !isResetButtonClick) {
          setVisible(false);
        }
      }
    };

    // Use capture phase to catch events before they bubble
    document.addEventListener('mousedown', handleClickOutside, true);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [visible]);

  const handleResetToDefault = useCallback(
    (onChange?: (value: string) => void) => {
      if (disabled) {
        return;
      }

      setColor(DEFAULT_GREEN);
      if (onChange) {
        onChange(DEFAULT_GREEN);
      }
    },
    [disabled],
  );

  // Memoize color change handlers
  const handleColorChange = useCallback((colorResult: { hex: string }, onChange: (value: string) => void) => {
    setColor(colorResult.hex);
    onChange(colorResult.hex);
  }, []);

  const errorMessage = get(errors, `${name}.message`) as string | undefined;
  const hasError = !!errorMessage;

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue || settings?.primaryColor1 || DEFAULT_GREEN}
      rules={validation}
      render={(controllerProps) => (
        <>
          <div className="new-account-image">
            <span id={id} className="round-icon" style={{ backgroundColor: color }}>
              <i className="icon2-color"></i>
            </span>
          </div>
          <div ref={triggerRef} onClick={handleTogglePopup} className="round-icon-small">
            <i className="icon-pencil"></i>
          </div>
          {resettableToDefaultColor && (
            <div
              ref={resetButtonRef}
              onClick={() => handleResetToDefault(controllerProps.onChange)}
              className="round-icon-small"
              style={{ marginLeft: '5px' }}
              title="Reset to default color"
            >
              <i className="icon-close"></i>
            </div>
          )}
          <div className="new-account-desc">
            {hasError && (
              <label htmlFor={name} style={{ color: 'red' }}>
                {`${label ? `${label} - ` : ''}${errorMessage}`}
              </label>
            )}
            <p className="bold">{placeholder}</p>
            <p className="small-text">{description}</p>
          </div>
          {visible && (
            <div
              ref={popupRef}
              className="color-picker-popup"
              style={{
                position: 'absolute',
                zIndex: 9999,
                ...(popupPosition === 'top' ? { bottom: '80px' } : { top: '100%', marginTop: '10px' }),
              }}
            >
              <ChromePicker
                disableAlpha={true}
                color={color}
                onChange={(colorResult) => handleColorChange(colorResult, controllerProps.onChange)}
                onChangeComplete={(colorResult) => handleColorChange(colorResult, controllerProps.onChange)}
              />
            </div>
          )}
        </>
      )}
    />
  );
};
